import { from, lastValueFrom, Observable, of, Subject, throwError } from 'rxjs';
import { AjaxError, AjaxRequest } from 'rxjs/ajax';
import { catchError, map, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { CancellationToken } from '../async/cancellation';
import { Progress } from '../async/progress';
import { ErrorMonitor } from '../diagnostics/error-monitor';
import { Strings } from '../generated/strings';
import { EnvironmentModule } from '../manifest/environment-modules';
import { AuthorizationManager } from '../security/authorization-manager';
import { Cookie } from './cookie';
import { GatewayConnection } from './gateway-connection';
import { ApiVersion } from './gateway-url-builder';
import { headerConstants, HttpResponseTypes, HttpStatusCode } from './http-constants';
import { NativeQ } from './native-q';
import { NodeConnection, NodeRequest } from './node-connection';
import { UriBuilder } from './uri-builder';

export interface FileOptions {
    /**
     * Indicates that audit logging for this request should be made. Default is false.
     */
    logAudit?: boolean;

    /**
     * Indicates that telemetry logging for this request should be made. Default is false.
     */
    logTelemetry?: boolean;
}

/** Represents a object that transfers files */
export class FileTransfer {

    private moduleName = null;

    /**
     * Downloads a blob of data
     *
     * @param blob the blob of data to download
     * @param fileName the name of the file for the user to download.
     */
    public static downloadBlob(blob: Blob, fileName: string) {
        let useAnchorTagForDownload = true;
        const windowNagivator: any = window.navigator;

        if (windowNagivator.msSaveOrOpenBlob) {
            // This is for IE and Microsoft Edge < 16
            // for those cases the download anchor tag doesn't generate the right name so we use the MS download system instead
            // "5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/51.0.2704.79 Safari/537.36 Edge/14.14393"
            const ua = navigator.userAgent;
            const edgeIndex = ua.indexOf('Edge');
            if (edgeIndex > 0) {
                const dotIndex = ua.indexOf('.', edgeIndex);
                let versionNumber = 0;
                if (dotIndex > 0) {
                    const versionString = ua.substring(edgeIndex + 'Edge'.length + 1, dotIndex);
                    versionNumber = Number(versionString);
                }

                useAnchorTagForDownload = versionNumber > 15;
            } else {
                useAnchorTagForDownload = false;
            }
        }

        if (useAnchorTagForDownload) {
            const downloadLink = document.createElement('a');
            downloadLink.style.display = 'none';

            const url = URL.createObjectURL(blob);
            downloadLink.setAttribute('href', url);
            downloadLink.setAttribute('download', fileName);
            downloadLink.click();
            downloadLink.remove();
        } else {
            windowNagivator.msSaveOrOpenBlob(blob, fileName);
        }
    }

    /**
     * Navigates to a file.
     *
     * @param filePath the file path we are navigating to.
     */
    public static navigateToFile(filePath: string): void {
        const downloadLink = document.createElement('a');
        downloadLink.style.display = 'none';
        downloadLink.setAttribute('href', filePath);
        downloadLink.click();
        downloadLink.remove();
    }

    /**
     * Initializes a new instance of the FileTransfer class.
     *
     * @param nodeConnection the NodeConnection class instance.
     * @param gatewayConnection the GatewayConnection class instance.
     * @param authorizationManager the AuthorizationManager class instance.
     */
    constructor(
        private nodeConnection: NodeConnection,
        private gatewayConnection: GatewayConnection,
        private authorizationManager: AuthorizationManager) {
    }

    private static generateRanges(ranges: ContentRange[], segmentSize: number, totalSize: number): void {
        const offset = segmentSize - 1;
        const max = totalSize - 1;

        for (let first = 0, last = offset; first < max; first += offset + 1, last = Math.min(++last + offset, max)) {
            ranges.push(new ContentRange(first, last, totalSize));
        }
    }

    private static extractNode(input: string) {
        const url = new URL(input);
        const segments = url.pathname.split('/');
        return segments[3];
    }

    /**
     * The GET call to file transfer endpoint and return a Blob of the requested file
     *
     * @param nodeName the node to transfer the file from.
     * @param sourcePath the path of the remote file to transfer.
     * @param fileOptions the file options for the action.
     * @return Observable<Blob> the observable Blob object.
     */
    public transferBlob(
        nodeName: string,
        sourcePath: string,
        fileOptions?: FileOptions): Observable<Blob> {
        const relativeUrl = this.gatewayConnection.url().node(nodeName).makeRelative().fileTransfer().file(sourcePath).build();
        const headers = {
            Accept: 'application/octet-stream'
        };
        const token = Cookie.getCrossSiteRequestForgeryToken();

        if (token) {
            headers[headerConstants.CROSS_SITE_REQUEST_FORGERY_TOKEN] = token;
        }

        const request = <NodeRequest><unknown>{ headers: headers, responseType: 'blob' };

        if (fileOptions) {
            request.logAudit = fileOptions.logAudit;
            request.logTelemetry = fileOptions.logTelemetry;
        }

        return this.nodeConnection.get(nodeName, relativeUrl, request);
    }

    /**
     * The GET call to file transfer endpoint and manual download of stream
     *
     * @param nodeName the node to transfer the file from.
     * @param sourcePath the path of the remote file to transfer.
     * @param targetName the desired name for the downloaded file.
     * @param fileOptions the file options for the action.
     * @return Observable<Blob> the observable Blob object.
     */
    public transferFile(
        nodeName: string,
        sourcePath: string,
        targetName: string,
        fileOptions?: FileOptions
    ): Observable<Blob> {

        return this.transferBlob(nodeName, sourcePath, fileOptions)
            .pipe(
                catchError((error) => {
                    if (error.response && error.response.type === HttpResponseTypes.Json) {
                        return from(error.response.text()).pipe(
                            mergeMap((errorJson: string) => throwError(JSON.parse(errorJson)?.error))
                        );
                    }

                    return throwError(error);
                }),
                map((responseBlob: Blob) => {
                    FileTransfer.downloadBlob(responseBlob, targetName);
                    return responseBlob;
                })
            );
    }

    /**
     * Upload a file from fileObject.
     *
     * @deprecated Use upload instead.
     * @param nodeName the node to upload the file to.
     * @param path the file path to store on the target node.
     * @param fileObject the file object created on the UI.
     * @param fileOptions the file options for the action.
     * @return Observable<any> the observable object.
     */
    public uploadFile(nodeName: string, path: string, fileObject: File, fileOptions?: FileOptions): Observable<any> {
        const deferred = NativeQ.defer<any>();
        const formData = new FormData();
        formData.append('file-0', fileObject);
        const request = new XMLHttpRequest();
        const url = this.gatewayConnection.url().node(nodeName).fileTransfer().file(path).build();
        const handler = () => {
            if (request.readyState === 4 /* complete */) {
                if (request.status === HttpStatusCode.Ok || request.status === HttpStatusCode.Created) {
                    deferred.resolve(request.responseText);
                } else {
                    ErrorMonitor.current.reportErrorFromAjax(
                        <AjaxError>{
                            status: request.status,
                            xhr: request,
                            request: { url, method: 'POST' }
                        });

                    const uploadError = MsftSme.getStrings<Strings>().MsftSmeShell.Core.DirectoryList.Upload.Error;
                    const message = request.status === HttpStatusCode.CorsRequestFailed ? uploadError.FileNotFound
                        : (request.status === HttpStatusCode.BadRequest ? uploadError.OperationBlocked
                            : uploadError.Unknown.format(request.status));
                    deferred.reject({ xhr: request, message: message });
                }
            }
        };

        let tokenValue: string;
        const ajaxRequest = <AjaxRequest>{ headers: {} };
        this.authorizationManager.addAuthorizationRequestHeader(ajaxRequest, nodeName);
        request.open('PUT', url);
        request.withCredentials = true;

        tokenValue = ajaxRequest.headers[headerConstants.SME_AUTHORIZATION];
        if (tokenValue) {
            request.setRequestHeader(headerConstants.SME_AUTHORIZATION, tokenValue);
        }

        tokenValue = ajaxRequest.headers[headerConstants.USE_LAPS];
        if (tokenValue) {
            request.setRequestHeader(headerConstants.USE_LAPS, tokenValue);

            // If ajaxRequest.headers[LAPS_LOCALADMINNAME] will always have default of 'administrator',
            // so no need to check if it exists and not null
            request.setRequestHeader(headerConstants.LAPS_LOCALADMINNAME, ajaxRequest.headers[headerConstants.LAPS_LOCALADMINNAME]);
        }

        if (fileOptions) {
            if (fileOptions.logAudit === true || fileOptions.logAudit === false) {
                request.setRequestHeader(headerConstants.LOG_AUDIT, fileOptions.logAudit ? 'true' : 'false');
            }

            if (fileOptions.logTelemetry === true || fileOptions.logTelemetry === false) {
                request.setRequestHeader(headerConstants.LOG_TELEMETRY, fileOptions.logTelemetry ? 'true' : 'false');
            }
        }

        const token = Cookie.getCrossSiteRequestForgeryToken();
        if (token) {
            request.setRequestHeader(headerConstants.CROSS_SITE_REQUEST_FORGERY_TOKEN, token);
        }

        request.setRequestHeader(headerConstants.MODULE_NAME, this.nameOfModule);
        request.onreadystatechange = handler;
        request.send(formData);
        return from(deferred.promise);
    }

    /**
     * Uploads the specified file.
     * @param node The target computer to upload the file to.
     * @param path The path on the target computer to upload the file to.
     * @param file The file to upload.
     * @param options The file upload options.
     * @param cancellationToken The token that can be used to cancel the operation.
     * @returns An observable object that contains the result of the file transfer.
     */
    public upload(
        node: string,
        path: string,
        file: File,
        options: FileUploadOptions = new FileUploadOptions(),
        cancellationToken: CancellationToken = CancellationToken.NONE
    ): Observable<FileTransferResult> {
        return from(this.uploadAsync(node, path, file, options, cancellationToken));
    }

    private uploadAsync(
        node: string,
        path: string,
        file: File,
        options: FileUploadOptions,
        cancellationToken: CancellationToken): Promise<FileTransferResult> {

        // if the file size <= transfer size, then just transfer the file all at once,
        // which will be much faster than the multi - request flow for a single transfer
        if (file.size <= options.transferSize) {
            return this.uploadAllAtOnce(node, path, file, options, cancellationToken);
        }

        return this.uploadInParts(node, path, file, options, cancellationToken);
    }

    private async uploadAllAtOnce(
        node: string,
        path: string,
        file: File,
        options: FileUploadOptions,
        cancellationToken: CancellationToken): Promise<FileTransferResult> {

        const url = this.gatewayConnection.url().node(node).fileTransfer().file(path).build();
        const headers = this.newRequestHeaders(node, options);
        const transfer = new FileTransferInfo(file.size, 1);
        const result = new FileTransferResult(new FileTransferContinuation(null, null, file, transfer));

        do {
            const response = await fetch(url, { method: 'PUT', credentials: 'include', headers: headers, body: file });
            switch (response.status) {
                case HttpStatusCode.Ok:
                case HttpStatusCode.Created:
                case HttpStatusCode.NoContent:
                    transfer.incrementCompleted();
                    return result;
                case HttpStatusCode.Forbidden:
                case HttpStatusCode.Conflict:
                    const blockedMessage = MsftSme.getStrings<Strings>().MsftSmeShell.Core.DirectoryList.Upload.Error.OperationBlocked;
                    throw new Error(blockedMessage);
                default:
                    if (cancellationToken.isCancellationRequested) {
                        break;
                    }
                    const unknownMessage = MsftSme.getStrings<Strings>().MsftSmeShell.Core.DirectoryList.Upload.Error.Unknown;
                    throw new Error(unknownMessage.format(response.status, response.statusText));
            }
        }
        while (!cancellationToken.isCancellationRequested);

        cancellationToken.throwIfCancellationRequested();
    }

    private async uploadInParts(
        node: string,
        path: string,
        file: File,
        options: FileUploadOptions,
        cancellationToken: CancellationToken = CancellationToken.NONE): Promise<FileTransferResult> {

        const continuation = await this.newUpload(node, path, file, options, cancellationToken);
        let canceled = cancellationToken.isCancellationRequested;
        let error: Error = null;

        if (!canceled) {
            error = await lastValueFrom(this.uploadRanges(continuation, options, cancellationToken));
            canceled = error !== null || cancellationToken.isCancellationRequested;
        }

        if (canceled && options.cancellationBehavior === FileTransferCancellationBehavior.Cancel) {
            try {
                // cancel the file transfer if an error occurred or the caller requested cancellation
                await this.cancelUpload(continuation);
            } catch (e) {
                // unable to cancel (e.g. clean up) the file transfer
                // if cancellation occurred from an error, rethrow it now
                if (error !== null) {
                    throw error;
                }

                throw e;
            }

            return new FileTransferResult();
        }

        return new FileTransferResult(continuation);
    }

    /**
     * Resumes a previously started file upload.
     * @param continuation The information required to continue the file transfer.
     * @param progress The object used to report the progress of the upload.
     * @param cancellationToken The token that can be used to cancel the operation.
     */
    public resumeUpload(
        continuation: FileTransferContinuation,
        progress: Progress<number> = new NoProgress(),
        cancellationToken: CancellationToken = CancellationToken.NONE): Observable<void> {

        return from(this.resumeUploadAsync(continuation, progress, cancellationToken));
    }

    private async resumeUploadAsync(
        continuation: FileTransferContinuation,
        progress: Progress<number> = new NoProgress(),
        cancellationToken: CancellationToken = CancellationToken.NONE): Promise<void> {

        const transferSize = continuation.transfer.size;
        const options = new FileUploadOptions(transferSize, FileTransferCancellationBehavior.Pause, progress);
        let error: Error = null;
        error = await lastValueFrom(this.uploadRanges(continuation, options, cancellationToken));

        if (error !== null) {
            throw error;
        }

        return null;
    }

    /**
     * Cancels a previously started file upload.
     * @param continuation The information required to cancel the file transfer.
     */
    public cancelUpload(continuation: FileTransferContinuation): Observable<void> {
        return from(this.cancelUploadAsync(continuation));
    }

    private async cancelUploadAsync(continuation: FileTransferContinuation): Promise<void> {
        const url = continuation.location;
        const node = FileTransfer.extractNode(url);
        const headers = this.newRequestHeaders(node, new FileUploadOptions());
        let attempts = 0;

        headers.append(headerConstants.IF_MATCH, continuation.token);

        do {
            const response = await fetch(url, { method: 'DELETE', credentials: 'include', headers: headers });
            const statusCode = response.status;

            // canceled or a new transfer was started
            if (statusCode === HttpStatusCode.NoContent || statusCode === HttpStatusCode.PreconditionFailed) {
                break;
            }
            switch (statusCode) {
                case HttpStatusCode.Conflict:
                    break;
                case HttpStatusCode.BadGateway:
                case HttpStatusCode.ServiceUnavailable:
                    if (++attempts > 3) {
                        const giveUpMessage = MsftSme.getStrings<Strings>().MsftSmeShell.Core.DirectoryList.Upload.Error.Unknown;
                        throw new Error(giveUpMessage.format(statusCode, response.statusText));
                    }
                    break;
                default:
                    const unknownMessage = MsftSme.getStrings<Strings>().MsftSmeShell.Core.DirectoryList.Upload.Error.Unknown;
                    throw new Error(unknownMessage.format(statusCode, response.statusText));
            }

        } while (true);

        return null;
    }

    private newRequestHeaders(node: string, options: FileUploadOptions): Headers {
        const request = this.gatewayConnection.defaultHttpSecureOptions;

        this.authorizationManager.addAuthorizationRequestHeader(request, node);

        const headers = new Headers();
        const authAadToken = request.headers[headerConstants.SME_AAD_AUTHORIZATION];
        const authToken = request.headers[headerConstants.SME_AUTHORIZATION];
        const useLaps = request.headers[headerConstants.USE_LAPS];
        const xsfrToken = Cookie.getCrossSiteRequestForgeryToken();
        const { logAudit, logTelemetry } = options.logging;

        headers.append(headerConstants.MODULE_NAME, this.nameOfModule);

        if (authAadToken) {
            headers.append(headerConstants.SME_AAD_AUTHORIZATION, authAadToken);
        }

        if (authToken) {
            headers.append(headerConstants.SME_AUTHORIZATION, authToken);
        }

        if (useLaps) {
            headers.append(headerConstants.USE_LAPS, useLaps);
            headers.append(headerConstants.LAPS_LOCALADMINNAME, request.headers[headerConstants.LAPS_LOCALADMINNAME]);
        }

        if (xsfrToken) {
            headers.append(headerConstants.CROSS_SITE_REQUEST_FORGERY_TOKEN, xsfrToken);
        }

        if (logAudit !== undefined) {
            headers.append(headerConstants.LOG_AUDIT, logAudit.toString());
        }

        if (logTelemetry !== undefined) {
            headers.append(headerConstants.LOG_TELEMETRY, logTelemetry.toString());
        }

        return headers;
    }

    private async newUpload(
        node: string,
        path: string,
        file: File,
        options: FileUploadOptions,
        cancellationToken: CancellationToken = CancellationToken.NONE): Promise<FileTransferContinuation> {

        const url = this.gatewayConnection.url().node(node).fileTransfer().file(path).build();
        const lastModified = new Date(file.lastModified).toISOString();
        const headers = this.newRequestHeaders(node, options);
        let response: Response;
        let retry = false;

        headers.append(headerConstants.CONTENT_DISPOSITION, `create; size=${file.size}; modification-date="${lastModified}"`);

        do {
            response = await fetch(url, { method: 'POST', credentials: 'include', headers: headers });
            retry = response.status === HttpStatusCode.Conflict;
        } while (retry && !cancellationToken.isCancellationRequested);

        switch (response.status) {
            case HttpStatusCode.Created:
                const builder = new UriBuilder(response.headers.get(headerConstants.LOCATION));

                // it's the client's job to tell the server which api version they want to use;
                // the location header will not contain the query parameter
                builder.setQueryParameter('api-version', ApiVersion.Latest);

                const location = builder.toString();
                const etag = response.headers.get(headerConstants.ETAG);
                const ranges = new Array<ContentRange>();

                FileTransfer.generateRanges(ranges, options.transferSize, file.size);

                const transfer = new FileTransferInfo(options.transferSize, ranges.length);
                const continuation = new FileTransferContinuation(location, etag, file, transfer, ranges);

                return continuation;
            case HttpStatusCode.Forbidden:
            case HttpStatusCode.Conflict:
                const blockedMessage = MsftSme.getStrings<Strings>().MsftSmeShell.Core.DirectoryList.Upload.Error.OperationBlocked;
                throw new Error(blockedMessage);
            default:
                const unknownMessage = MsftSme.getStrings<Strings>().MsftSmeShell.Core.DirectoryList.Upload.Error.Unknown;
                throw new Error(unknownMessage.format(response.status, response.statusText));
        }
    }

    private uploadRanges(
        continuation: FileTransferContinuation,
        options: FileUploadOptions,
        cancellationToken: CancellationToken): Observable<Error> {

        const node = FileTransfer.extractNode(continuation.location);
        const progress = options.progress;
        const ranges = [...continuation.ranges];
        const transfer = continuation.transfer;

        const cancellationObservable = new Subject<void>();

        if (cancellationToken.isCancellationRequested) {
            // Emit to trigger the cancellation
            cancellationObservable.next();
            cancellationObservable.complete();
        }
        // Creating an observable that emits the index and the range
        const uploadStream = from(ranges).pipe(
            // Limit concurrency to 3 with mergeMap
            mergeMap((range, index) => {
                return this.uploadPartialFileHelper(index, node, continuation, range, options).pipe(
                    tap(result => {
                        switch (result.statusCode) {
                            // No Content (success)
                            case 204:
                                transfer.incrementCompleted();
                                progress.report(transfer.percentComplete);
                                break;
                            case HttpStatusCode.ServiceUnavailable:
                                if (!cancellationToken.isCancellationRequested) {
                                    // retry upload
                                    return this.uploadPartialFileHelper(index, node, continuation, range, options);
                                }
                                break;
                            case HttpStatusCode.Unauthorized:
                            case HttpStatusCode.Conflict:
                            case HttpStatusCode.BadGateway:
                            case HttpStatusCode.Gone:
                            case HttpStatusCode.PreconditionFailed:
                            default:
                                const unknownMessage = MsftSme.getStrings<Strings>().MsftSmeShell.Core.DirectoryList.Upload.Error.UnknownNoMessage;
                                throw new Error(unknownMessage.format(result.statusCode));
                        }
                    })
                );
            // 3 parallel uploads
            }, 3)
        );

        return uploadStream.pipe(
            takeUntil(cancellationObservable),
            catchError(err => {
                // If an error occurs during the upload process, emit it
                return of(err);
            }));
    }

    private uploadPartialFileHelper(index: number,
        node: string,
        continuation: FileTransferContinuation,
        range: ContentRange,
        options: FileUploadOptions): Observable<HttpTransferResult> {
        return from(this.uploadPartialFile(index, node, continuation, range, options));
    }

    private async uploadPartialFile(
        index: number,
        node: string,
        continuation: FileTransferContinuation,
        range: ContentRange,
        options: FileUploadOptions): Promise<HttpTransferResult> {

        const blob = continuation.file.slice(range.from, range.to + 1);
        const headers = this.newRequestHeaders(node, options);

        headers.append(headerConstants.CONTENT_TYPE, 'application/octet-stream');
        headers.append(headerConstants.CONTENT_RANGE, range.toString());
        headers.append(headerConstants.IF_MATCH, continuation.token);

        const response = await fetch(continuation.location, {
            method: 'PATCH',
            credentials: 'include',
            headers: headers,
            body: blob
        });

        return new HttpTransferResult(index, response.status, range);
    }

    /**
     * Gets the name of current shell or module.
     */
    private get nameOfModule(): string {
        if (!this.moduleName) {
            this.moduleName = EnvironmentModule.getModuleName();
        }

        return this.moduleName;
    }
}

/** Represents the possible cancellation behaviors */
export enum FileTransferCancellationBehavior {
    /** Indicates the file transfer is cancelled or aborted on cancellation */
    Cancel,

    /** Indicates the file transfer is paused on cancellation */
    Pause,
}

/** Represents file upload options. */
export class FileUploadOptions {
    constructor(
        /** Gets the size of transfer content. The default value is 10MB. */
        public readonly transferSize: number = 10485760,

        /** Indicates the behavior to perform if the operation is canceled. */
        public readonly cancellationBehavior: FileTransferCancellationBehavior = FileTransferCancellationBehavior.Cancel,

        /** A token that can be used to cancel the operation. */
        public readonly progress: Progress<number> = new NoProgress(),

        /** Gets the associated logging options. */
        public readonly logging: FileOptions = { logAudit: false, logTelemetry: false }) {

    }
}

/** Represents the size and progress of a file transfer. */
export class FileTransferInfo {
    private completed = 0;

    constructor(
        /** Gets the size used in the file transfer. */
        public readonly size: number,

        /** Gets the total number of items in the file transfer. */
        public readonly count: number) {
    }

    /** Gets the percentage of the file transfer completed. */
    public get percentComplete(): number {
        return (this.completed / this.count) * 100;
    }

    /** Increments the number of completed items in the file transfer. */
    public incrementCompleted(): void {
        this.completed = Math.min(this.completed + 1, this.count);
    }
}

/** Represents a continuation for a file transfer */
export class FileTransferContinuation {
    constructor(
        /** Gets the URl representing the location of the file transfer */
        public readonly location: string,

        /** Gets the continuation token for the file transfer */
        public readonly token: string,

        /** Gets the file being transferred */
        public readonly file: File,

        /** Gets the file transfer information. */
        public readonly transfer: FileTransferInfo,

        /** Gets the remaining ranges in the file transfer */
        public readonly ranges: ContentRange[] = new Array<ContentRange>()) {
    }
}

/** Represents the result of a file transfer */
export class FileTransferResult {
    constructor(
        /** Gets the continuation token associated with the file transfer */
        public readonly continuation: FileTransferContinuation =
            new FileTransferContinuation(null, null, null, new FileTransferInfo(0, 0))) {

    }

    /** Gets a value indicating whether the file transfer is complete */
    public get completed(): boolean {
        return this.continuation.ranges.length === 0;
    }

    /** Gets a value indicating whether the file transfer is resumable */
    public get resumable() {
        return !this.completed &&
            this.continuation.token !== null &&
            this.continuation.location !== null &&
            this.continuation.file !== null;
    }
}

/** Represent a range of content in a file transfer */
export class ContentRange {
    constructor(
        /** Gets the zero-based start of the content, in bytes */
        public readonly from: number,

        /** Gets the zero-based end of the content, in bytes */
        public readonly to: number,

        /** Gets the total size of the content in bytes */
        public readonly totalSize: number) {

    }

    /** Returns the string representation of the object */
    public toString(): string {
        return `bytes ${this.from}-${this.to}/${this.totalSize}`;
    }
}

class NoProgress implements Progress<number> {
    public report(): void {

    }
}

class HttpTransferResult {
    constructor(
        public readonly index: number,
        public readonly statusCode: number,
        public readonly range: ContentRange) {

    }
}
